import React from 'react';
import { Link } from 'react-router-dom';
import {
  ShieldCheck,
  Award,
  Sparkles,
  Zap,
  Moon,
  Clock,
  ArrowRight,
  Flame,
  Globe,
  Users,
  CheckCircle2,
  ChevronRight,
  FileText
} from 'lucide-react';

const Science = () => {
  const stats = [
    { num: '500+', label: 'Studies Conducted', icon: Users },
    { num: '10,000+', label: 'Participants', icon: Sparkles },
    { num: '25+', label: 'Years of Research', icon: Globe },
    { num: '90%', label: 'Positive Outcomes', icon: Award },
  ];

  const howItWorksSteps = [
    {
      num: '01',
      title: 'Light Penetrates',
      desc: 'Red & near-infrared light penetrates deep into the skin.',
      icon: Flame
    },
    {
      num: '02',
      title: 'Cellular Absorption',
      desc: 'Light is absorbed by mitochondria within cells.',
      icon: Sparkles
    },
    {
      num: '03',
      title: 'Increased ATP',
      desc: 'Boosts ATP production, the energy your cells need.',
      icon: Zap
    },
    {
      num: '04',
      title: 'Therapeutic Benefits',
      desc: 'Supports healing, reduces inflammation, and enhances overall wellness.',
      icon: ShieldCheck
    }
  ];

  const benefits = [
    {
      title: 'Reduces Inflammation',
      desc: 'Helps reduce inflammation and supports faster recovery.',
      icon: Flame,
      color: '#dc2626'
    },
    {
      title: 'Boosts Cellular Energy',
      desc: 'Increases ATP production for more energy and better cell function.',
      icon: Zap,
      color: '#dc2626'
    },
    {
      title: 'Enhances Recovery',
      desc: 'Speeds up muscle recovery and reduces soreness after physical activity.',
      icon: Sparkles,
      color: '#dc2626'
    },
    {
      title: 'Supports Skin Health',
      desc: 'Promotes collagen production and improves skin tone, texture, and elasticity.',
      icon: Award,
      color: '#dc2626'
    },
    {
      title: 'Improves Sleep & Mood',
      desc: 'Helps regulate circadian rhythm for better sleep and improved mood.',
      icon: Moon,
      color: '#dc2626'
    },
    {
      title: 'Anti-Aging Benefits',
      desc: 'Supports healthy aging by reducing oxidative stress at the cellular level.',
      icon: ShieldCheck,
      color: '#dc2626'
    }
  ];

  const processSteps = [
    {
      step: '1',
      title: 'Light Penetrates',
      desc: 'Red and near-infrared light penetrates the skin and reaches deep into tissues.',
      img: '/hero_panel_isolated_red_1787202436683.jpg'
    },
    {
      step: '2',
      title: 'Cellular Absorption',
      desc: 'The light is absorbed by mitochondria, the powerhouse of your cells.',
      img: '/mitochondria_cellular_absorption_1787559198177.jpg'
    },
    {
      step: '3',
      title: 'Increased ATP',
      desc: 'Mitochondria use the light energy to produce more ATP (energy).',
      img: '/atp_battery_icon_1787559258877.jpg'
    },
    {
      step: '4',
      title: 'Therapeutic Benefits',
      desc: 'Increased energy leads to healing, reduced inflammation and overall wellness.',
      img: '/red_light_woman_session_1787559237443.jpg'
    }
  ];

  const safetyGuarantees = [
    'FDA Cleared Devices',
    'Clinically Tested',
    'Non-Invasive & Safe',
    'No Harmful Chemicals',
    '2 Year Warranty',
    '60 Day Returns'
  ];

  return (
    <div className="science-page-wrapper" style={{ background: '#ffffff', fontFamily: "'Plus Jakarta Sans', sans-serif", color: '#0f172a' }}>
      
      {/* 1. HERO BANNER - BACKED BY SCIENCE. DRIVEN BY RESULTS. */}
      <section className="science-hero-section" style={{ background: '#ffffff', color: '#0f172a', position: 'relative', overflow: 'hidden', padding: '60px 24px 80px', borderBottom: '1px solid #f1f5f9' }}>
        
        {/* Generated White Background with Red Light Laser Beams & Waves Overlay (Same as Home Banner) */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: `linear-gradient(to right, rgba(255, 255, 255, 0.94) 25%, rgba(255, 255, 255, 0.65) 55%, rgba(255, 255, 255, 0.82) 100%), url('/hero_white_laser_bg_1787202417710.jpg')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center right',
          backgroundRepeat: 'no-repeat',
          opacity: 0.95,
          pointerEvents: 'none',
          zIndex: 1
        }}></div>

        {/* Red Light Ambient Radial Glow on Right Side */}
        <div style={{ position: 'absolute', top: '10%', right: '5%', width: '600px', height: '600px', background: 'radial-gradient(circle, rgba(239, 68, 68, 0.22) 0%, rgba(220, 38, 38, 0.05) 50%, transparent 80%)', filter: 'blur(60px)', pointerEvents: 'none', zIndex: 1 }}></div>

        <div className="science-hero-grid" style={{ maxWidth: '1240px', margin: '0 auto', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '40px', alignItems: 'center', position: 'relative', zIndex: 2 }}>
          
          {/* Left Text */}
          <div>
            <span style={{ fontSize: '12px', fontWeight: 800, color: '#dc2626', letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: '16px', display: 'block' }}>
              SCIENCE BEHIND THE LIGHT
            </span>
            <h1 className="science-hero-title" style={{ fontSize: '48px', fontWeight: 900, color: '#0f172a', lineHeight: 1.1, marginBottom: '24px', letterSpacing: '-0.03em' }}>
              Backed by Science.<br />
              Driven by <span style={{ color: '#dc2626' }}>Results</span>.
            </h1>
            <p style={{ fontSize: '15.5px', color: '#475569', lineHeight: 1.7, marginBottom: '36px', maxWidth: '480px' }}>
              Red light therapy is supported by decades of scientific research showing its powerful impact on cellular health, recovery, and overall wellbeing.
            </p>

            {/* Badges Grid */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '16px', maxWidth: '440px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div style={{ width: '36px', height: '36px', borderRadius: '10px', background: '#fef2f2', border: '1px solid #fecaca', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#dc2626' }}>
                  <Award size={18} />
                </div>
                <span style={{ fontSize: '12.5px', fontWeight: 800, color: '#0f172a' }}>Clinically Proven</span>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div style={{ width: '36px', height: '36px', borderRadius: '10px', background: '#fef2f2', border: '1px solid #fecaca', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#dc2626' }}>
                  <ShieldCheck size={18} />
                </div>
                <span style={{ fontSize: '12.5px', fontWeight: 800, color: '#0f172a' }}>Non-Invasive & Safe</span>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div style={{ width: '36px', height: '36px', borderRadius: '10px', background: '#fef2f2', border: '1px solid #fecaca', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#dc2626' }}>
                  <CheckCircle2 size={18} />
                </div>
                <span style={{ fontSize: '12.5px', fontWeight: 800, color: '#0f172a' }}>Drug-Free Therapy</span>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div style={{ width: '36px', height: '36px', borderRadius: '10px', background: '#fef2f2', border: '1px solid #fecaca', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#dc2626' }}>
                  <Sparkles size={18} />
                </div>
                <span style={{ fontSize: '12.5px', fontWeight: 800, color: '#0f172a' }}>Trusted by Experts</span>
              </div>
            </div>
          </div>

          {/* Right Image: Woman Sleeping under Red Light Therapy */}
          <div style={{ textAlign: 'center' }}>
            <img
              src="/hero_woman_sleeping_red_light.jpg"
              alt="Deep Sleep & Cellular Recovery with Red Light Therapy"
              style={{ width: '100%', maxWidth: '520px', maxHeight: '360px', objectFit: 'cover', borderRadius: '24px', boxShadow: '0 12px 35px rgba(220,38,38,0.15)', border: '1px solid rgba(220, 38, 38, 0.15)' }}
            />
          </div>

        </div>
      </section>

      {/* 2. STATS BAR - 500+ STUDIES / 10,000+ PARTICIPANTS */}
      <section style={{ background: '#fafbfc', borderBottom: '1px solid #e2e8f0', padding: '36px 24px' }}>
        <div style={{ maxWidth: '1240px', margin: '0 auto', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '24px' }}>
          {stats.map((st, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '16px', borderRight: i < 3 ? '1px solid #e2e8f0' : 'none', paddingRight: '16px' }}>
              <div style={{ width: '48px', height: '48px', borderRadius: '12px', background: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <st.icon size={22} />
              </div>
              <div>
                <div style={{ fontSize: '26px', fontWeight: 900, color: '#0f172a', lineHeight: 1.1 }}>{st.num}</div>
                <div style={{ fontSize: '12.5px', color: '#64748b', fontWeight: 700, marginTop: '2px' }}>{st.label}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* 3. KEY BENEFITS OF RED LIGHT THERAPY (6 CARDS GRID) */}
      <section style={{ padding: '60px 24px', background: '#ffffff' }}>
        <div style={{ maxWidth: '1240px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '40px' }}>
            <h2 style={{ fontSize: '32px', fontWeight: 900, color: '#0f172a', marginBottom: '8px' }}>
              Key Benefits of Red Light Therapy
            </h2>
            <div style={{ width: '40px', height: '3px', background: '#dc2626', margin: '0 auto', borderRadius: '2px' }} />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: '20px' }}>
            {benefits.map((b, i) => {
              const Icon = b.icon;
              return (
                <div key={i} style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '16px', padding: '24px 16px', textAlign: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.02)', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <div style={{ width: '44px', height: '44px', borderRadius: '12px', background: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '16px' }}>
                    <Icon size={20} />
                  </div>
                  <h3 style={{ fontSize: '14px', fontWeight: 800, color: '#0f172a', marginBottom: '8px', lineHeight: 1.3 }}>
                    {b.title}
                  </h3>
                  <p style={{ fontSize: '11.5px', color: '#64748b', lineHeight: 1.5, margin: 0 }}>
                    {b.desc}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* 4. HOW RED LIGHT THERAPY WORKS (4 STEP CARDS GRID MATCHING SCIENC.PNG) */}
      <section style={{ padding: '60px 24px', background: '#fafbfc', borderTop: '1px solid #f1f5f9', borderBottom: '1px solid #f1f5f9' }}>
        <div style={{ maxWidth: '1240px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '40px' }}>
            <h2 style={{ fontSize: '32px', fontWeight: 900, color: '#0f172a', marginBottom: '8px' }}>
              How Red Light Therapy Works
            </h2>
            <div style={{ width: '40px', height: '3px', background: '#dc2626', margin: '0 auto', borderRadius: '2px' }} />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '24px', position: 'relative' }}>
            {processSteps.map((step) => (
              <div key={step.step} style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '20px', overflow: 'hidden', textAlign: 'center', padding: '24px 20px', boxShadow: '0 4px 12px rgba(0,0,0,0.03)', position: 'relative' }}>
                
                {/* Step Badge */}
                <div style={{ position: 'absolute', top: '16px', left: '16px', width: '28px', height: '28px', borderRadius: '50%', background: '#dc2626', color: '#ffffff', fontSize: '12px', fontWeight: 900, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {step.step}
                </div>

                {/* Step Visual */}
                <div style={{ height: '140px', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '16px', marginTop: '12px' }}>
                  <img src={step.img} alt={step.title} style={{ maxHeight: '130px', maxWidth: '100%', objectFit: 'contain', borderRadius: '12px' }} />
                </div>

                <h4 style={{ fontSize: '15px', fontWeight: 900, color: '#0f172a', marginBottom: '6px' }}>{step.title}</h4>
                <p style={{ fontSize: '12px', color: '#64748b', lineHeight: 1.5, margin: 0 }}>{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 5. OPTIMAL WAVELENGTHS. PROVEN RESULTS. */}
      <section style={{ padding: '60px 24px', background: '#ffffff' }}>
        <div style={{ maxWidth: '800px', margin: '0 auto', textAlign: 'center' }}>
          <span style={{ fontSize: '12px', fontWeight: 800, color: '#dc2626', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '12px', display: 'block' }}>
            THE SCIENCE
          </span>
          <h2 style={{ fontSize: '32px', fontWeight: 900, color: '#0f172a', lineHeight: 1.25, marginBottom: '16px' }}>
            Optimal Wavelengths. Proven Results.
          </h2>
          <p style={{ fontSize: '15px', color: '#475569', lineHeight: 1.7, marginBottom: '36px' }}>
            InfraEdge devices use clinically studied wavelengths that deliver optimal therapeutic benefits deep into cellular tissue.
          </p>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '24px', textAlign: 'left' }}>
            <div style={{ background: '#fef2f2', border: '1px solid #fee2e2', borderRadius: '16px', padding: '24px' }}>
              <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '8px' }}>
                <div style={{ width: '12px', height: '12px', borderRadius: '50%', background: '#dc2626' }} />
                <h4 style={{ fontSize: '16px', fontWeight: 800, color: '#0f172a', margin: 0 }}>660nm Red Light</h4>
              </div>
              <p style={{ fontSize: '13.5px', color: '#64748b', margin: 0, lineHeight: 1.6 }}>
                Visible red light shown to support skin health, collagen production, and overall cellular function.
              </p>
            </div>

            <div style={{ background: '#fef2f2', border: '1px solid #fee2e2', borderRadius: '16px', padding: '24px' }}>
              <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '8px' }}>
                <div style={{ width: '12px', height: '12px', borderRadius: '50%', background: '#b91c1c' }} />
                <h4 style={{ fontSize: '16px', fontWeight: 800, color: '#0f172a', margin: 0 }}>850nm Near-Infrared Light</h4>
              </div>
              <p style={{ fontSize: '13.5px', color: '#64748b', margin: 0, lineHeight: 1.6 }}>
                Invisible light that penetrates deeper into tissues and muscles for pain relief and recovery.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 6. SAFETY & QUALITY YOU CAN TRUST */}
      <section style={{ padding: '60px 24px', background: '#fafbfc', borderTop: '1px solid #f1f5f9' }}>
        <div style={{ maxWidth: '1240px', margin: '0 auto' }}>
          
          <div style={{ textAlign: 'center', marginBottom: '36px' }}>
            <h2 style={{ fontSize: '30px', fontWeight: 900, color: '#0f172a', marginBottom: '8px' }}>
              Safety & Quality You Can Trust
            </h2>
            <div style={{ width: '40px', height: '3px', background: '#dc2626', margin: '0 auto', borderRadius: '2px' }} />
          </div>

          {/* Guarantee Badges Row */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '16px', marginBottom: '40px' }}>
            {safetyGuarantees.map((item, idx) => (
              <div key={idx} style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '16px 12px', textAlign: 'center', fontSize: '12.5px', fontWeight: 800, color: '#0f172a', boxShadow: '0 2px 6px rgba(0,0,0,0.02)' }}>
                {item}
              </div>
            ))}
          </div>

          {/* Safe, Effective & Backed by Science Banner */}
          <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '24px', padding: '32px 36px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '24px' }}>
            <div style={{ maxWidth: '540px' }}>
              <h3 style={{ fontSize: '22px', fontWeight: 900, color: '#0f172a', marginBottom: '10px' }}>
                Safe, Effective & Backed by Science
              </h3>
              <p style={{ fontSize: '14px', color: '#475569', lineHeight: 1.6, marginBottom: '20px' }}>
                InfraEdge devices are FDA cleared, clinically tested, and trusted by thousands worldwide to deliver real results.
              </p>
              <Link to="/shop" style={{ background: '#dc2626', color: '#ffffff', padding: '12px 24px', borderRadius: '9999px', fontSize: '13.5px', fontWeight: 800, textDecoration: 'none', display: 'inline-block' }}>
                Learn More About Our Devices
              </Link>
            </div>

            {/* Certificates Gallery Row */}
            <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
              <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '12px 18px', boxShadow: '0 4px 12px rgba(0,0,0,0.04)', display: 'flex', alignItems: 'center', gap: '12px' }}>
                <Award size={32} color="#dc2626" />
                <div>
                  <div style={{ fontSize: '13px', fontWeight: 900, color: '#0f172a', textTransform: 'uppercase', letterSpacing: '0.05em' }}>CERTIFIED MEDICAL GRADE</div>
                  <div style={{ fontSize: '11px', color: '#64748b', fontWeight: 700 }}>FDA Cleared • ISO 13485 • CE Safety Compliant</div>
                </div>
              </div>
            </div>
          </div>

        </div>
      </section>

      {/* 7. BOTTOM CALLOUT BANNER - EXPERIENCE THE POWER OF LIGHT */}
      <section style={{ padding: '0 24px 60px' }}>
        <div style={{ maxWidth: '1240px', margin: '0 auto', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '24px', overflow: 'hidden', position: 'relative', padding: '48px 44px' }}>
          
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '40px', alignItems: 'center', position: 'relative', zIndex: 2 }}>
            <div>
              <span style={{ fontSize: '11.5px', fontWeight: 800, color: '#dc2626', letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: '12px', display: 'block' }}>
                TRANSFORM YOUR HEALTH
              </span>
              <h2 style={{ fontSize: '34px', fontWeight: 900, color: '#0f172a', lineHeight: 1.2, marginBottom: '16px' }}>
                Experience the Power of Light
              </h2>
              <p style={{ fontSize: '15px', color: '#475569', lineHeight: 1.7, marginBottom: '28px', maxWidth: '480px' }}>
                Join thousands who trust InfraEdge for better health, faster recovery, and a better quality of life.
              </p>
              <Link to="/shop" style={{ background: '#dc2626', color: '#ffffff', padding: '14px 32px', borderRadius: '9999px', fontSize: '14.5px', fontWeight: 800, textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '8px', boxShadow: '0 10px 25px -5px rgba(220, 38, 38, 0.3)' }}>
                <span>Explore Devices</span>
                <ArrowRight size={16} />
              </Link>
            </div>

            <div style={{ textAlign: 'center' }}>
              <img
                src="/experience_light_white_bg.jpg"
                alt="Experience Red Light Therapy"
                style={{ width: '100%', maxHeight: '300px', objectFit: 'cover', borderRadius: '20px', boxShadow: '0 10px 30px rgba(220, 38, 38, 0.12)' }}
              />
            </div>
          </div>

        </div>
      </section>

    </div>
  );
};

export default Science;
